import { cartAtom } from "@/atoms"
import type { WonPrize } from "@/domain/marketing/api"
import categoriesAtom from "@/atoms/categories"
import { prizeForCode } from "@/domain/marketing/won-prize"
import { evaluateBagCoupons } from "@/lib/bag-coupon-source"
import { getMerchantId } from "@/lib/company-storage"
import { decideCouponArrival, replaceStashedCoupons } from "@/lib/coupon-arrival"
import { buildEvaluationLines } from "@/lib/discount-evaluation/cart-lines"
import type { CouponEvaluation } from "@/lib/discount-evaluation/types"
import { attachedCouponCodes, couponCodeKey } from "@/lib/pending-coupon"
import { useStore } from "jotai"
import { useCallback } from "react"
import { useAuthStatus } from "./use-auth-status"
import { reloadWonPrizes, wonPrizesAtom } from "./use-won-prizes"

/** The coupons a code replaced when it arrived (empty when it rides along with them). */
export type CouponArrival = { replaced: string[] }

/**
 * Settles a code that just reached the order (a `?cupom=` link or the bag
 * field) against the coupons already on it: coupon-service evaluates them all
 * on this bag and the newest replaces the others unless they combine (see
 * `decideCouponArrival`). Won prizes are never replaced.
 */
export function useCouponArrival(): (code: string) => Promise<CouponArrival> {
  const store = useStore()
  const authenticated = useAuthStatus()
  return useCallback((code: string) => settleArrival(store, authenticated, code), [store, authenticated])
}

type Store = ReturnType<typeof useStore>

async function wonPrizes(store: Store, authenticated: boolean): Promise<WonPrize[] | null> {
  if (!authenticated) return []
  const known = store.get(wonPrizesAtom)
  return known.status === "ready" ? known.prizes : reloadWonPrizes(store)
}

async function verdictFor(store: Store, authenticated: boolean, merchantId: string, codes: string[]): Promise<CouponEvaluation | null> {
  const lines = buildEvaluationLines(store.get(cartAtom), store.get(categoriesAtom))
  if (lines.length === 0) return null
  const request = { couponCodes: codes, lines, deliveryFeeCents: 0, cartBenefitDiscountCents: 0, deliveryBenefitDiscountCents: 0 }
  try {
    const { evaluation } = await evaluateBagCoupons({ merchantId, request, authenticated, terms: {}, now: new Date(), signal: new AbortController().signal })
    return evaluation
  } catch {
    return null
  }
}

async function settleArrival(store: Store, authenticated: boolean, code: string): Promise<CouponArrival> {
  const merchantId = getMerchantId()
  if (!merchantId) return { replaced: [] }
  const prizes = await wonPrizes(store, authenticated)
  if (prizes === null) return { replaced: [] }
  const existing = attachedCouponCodes(merchantId).filter((entry) => couponCodeKey(entry) !== couponCodeKey(code) && !prizeForCode(prizes, entry))
  if (existing.length === 0) return { replaced: [] }
  const decision = decideCouponArrival(code, existing, await verdictFor(store, authenticated, merchantId, [...existing, code]))
  if (decision === "combine") return { replaced: [] }
  replaceStashedCoupons(merchantId, code, existing)
  return { replaced: existing }
}
